import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import {
  PssAlertModalComponent,
  PssAlertModalVariant,
} from '../../../../tools/template/pss/pss-alert-modal/pss-alert-modal.component';
import { ApiSystemSettingsService } from '../../../../core/services/api/system-settings/api-system-settings.service';
import { ApiService } from '../../../../core/services/api.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { ClientIInfo } from '../../../../core/models/parking.model';
import {
  AccessGroupScope,
  ValidateFreeParkingAdvancePayload,
  mapAccessGroupScope,
} from '../../../../core/models/system-settings/access-group.model';
import {
  FreeParkingSubscriptionOption,
  mapFreeParkingSubscriptionList,
} from '../../../../core/models/system-settings/free-quota.model';

interface ValidateResultPopup {
  ok: boolean;
  title: string;
  message: string;
  remaining: number | null;
  clientName: string;
  subscriptionName: string;
  vehicleTypeName: string;
  qty: number;
}

@Component({
  selector: 'app-free-parking-advances',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, PssLoadingComponent, PssAlertModalComponent],
  templateUrl: './free-parking-advances.component.html',
  styleUrl: './free-parking-advances.component.css',
})
export class FreeParkingAdvancesComponent implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiSystemSettingsService);
  private readonly apiLegacy = inject(ApiService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);
  private readonly cookieService = inject(CookieService);

  scope = signal<AccessGroupScope | null>(null);
  allClients = signal<ClientIInfo[]>([]);
  allSubscriptions = signal<FreeParkingSubscriptionOption[]>([]);
  isLoading = signal(false);
  isValidating = signal(false);

  clientId = 0;
  subscriptionId = 0;
  vehicleTypeId = 0;
  vehicleTypeName = '';
  qty = 1;
  remark = '';

  showAlert = false;
  alertTitle = '';
  alertMessage = '';
  alertVariant: PssAlertModalVariant = 'warning';

  resultPopup: ValidateResultPopup | null = null;

  constructor() {
    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id) {
        untracked(() => {
          this.scope.set(null);
          this.allClients.set([]);
          this.allSubscriptions.set([]);
          this.resetForm();
        });
        return;
      }
      untracked(() => this.loadAllForSite(site.id));
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get selectedSiteName(): string {
    return this.siteState.selectedSite()?.name ?? '';
  }

  get hasGroup(): boolean {
    return !!(this.scope()?.groupId);
  }

  get canManageFreeCards(): boolean {
    return !!this.scope()?.canManageFreeCards;
  }

  get scopedClients(): ClientIInfo[] {
    const scope = this.scope();
    const clients = this.allClients();
    if (!scope?.clientIds?.length) return clients;
    const allowed = new Set(scope.clientIds);
    return clients.filter((c) => allowed.has(c.id));
  }

  get scopedSubscriptions(): FreeParkingSubscriptionOption[] {
    const scope = this.scope();
    const subs = this.allSubscriptions();
    if (!scope?.subscriptionIds?.length) return subs;
    const allowed = new Set(scope.subscriptionIds);
    return subs.filter((s) => allowed.has(s.id));
  }

  get canSubmit(): boolean {
    return (
      this.canManageFreeCards &&
      !this.isValidating() &&
      this.clientId > 0 &&
      this.subscriptionId > 0 &&
      this.vehicleTypeId > 0 &&
      this.qty > 0
    );
  }

  ngOnInit(): void {
    if (this.selectedSiteId) {
      this.loadAllForSite(this.selectedSiteId);
    }
  }

  onSubscriptionChange(): void {
    const sub = this.scopedSubscriptions.find((s) => s.id === this.subscriptionId);
    this.vehicleTypeId = sub?.vehicleTypeId ?? 0;
    this.vehicleTypeName = sub?.vehicleTypeName ?? '';
  }

  onValidate(): void {
    if (!this.canManageFreeCards) {
      this.openAlert('warning', 'ไม่มีสิทธิ์', 'บัญชีนี้ไม่มีสิทธิ์จัดการบัตรจอดฟรี');
      return;
    }
    const siteId = this.selectedSiteId;
    if (!siteId) {
      this.openAlert('warning', 'ยังไม่ได้เลือกลาน', 'กรุณาเลือกลานจากแถบด้านข้างก่อน');
      return;
    }
    if (!this.clientId || !this.subscriptionId || !this.vehicleTypeId || this.qty <= 0) {
      this.openAlert('warning', 'ข้อมูลไม่ครบ', 'กรุณาเลือกร้าน · Subscription · จำนวนบัตรให้ครบ');
      return;
    }

    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) {
      this.openAlert('error', 'ไม่พบสิทธิ์เมนู', 'ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู — ลองเข้าสู่ระบบใหม่');
      return;
    }

    const client = this.scopedClients.find((c) => c.id === this.clientId);
    const sub = this.scopedSubscriptions.find((s) => s.id === this.subscriptionId);

    const payload: ValidateFreeParkingAdvancePayload = {
      userId,
      menuId,
      siteId,
      clientId: this.clientId,
      subscriptionId: this.subscriptionId,
      vehicleTypeId: this.vehicleTypeId,
      qty: this.qty,
      remark: this.remark?.trim() || null,
    };

    this.isValidating.set(true);
    this.api.validateFreeParkingAdvance(payload).subscribe({
      next: (res) => {
        const ok = !!(res?.isSuccess ?? res?.IsSuccess);
        const msg = String(res?.message ?? res?.Message ?? '');
        const remainingRaw = res?.idReturn ?? res?.IdReturn;
        const remaining =
          remainingRaw != null && remainingRaw !== undefined && remainingRaw !== ''
            ? Number(remainingRaw)
            : null;

        this.resultPopup = {
          ok,
          title: ok ? 'ผ่านการตรวจสอบ' : 'ตรวจสอบไม่ผ่าน',
          message: msg || (ok ? 'โควต้าเพียงพอสำหรับจำนวนที่ระบุ' : 'ไม่สามารถดำเนินการต่อได้'),
          remaining: Number.isFinite(remaining as number) ? (remaining as number) : null,
          clientName: client?.name ?? `Client #${this.clientId}`,
          subscriptionName: sub?.name ?? `Subscription #${this.subscriptionId}`,
          vehicleTypeName: this.vehicleTypeName || sub?.vehicleTypeName || '—',
          qty: this.qty,
        };
        this.isValidating.set(false);
      },
      error: () => {
        this.isValidating.set(false);
        this.openAlert('error', 'เกิดข้อผิดพลาด', 'ไม่สามารถตรวจสอบโควต้าได้ในขณะนี้ ลองใหม่อีกครั้ง');
      },
    });
  }

  closeResultPopup(): void {
    this.resultPopup = null;
  }

  goCreateAdvance(): void {
    this.resultPopup = null;
    void this.router.navigate(['/cityparking/card-advances']);
  }

  closeAlert(): void {
    this.showAlert = false;
  }

  private openAlert(variant: PssAlertModalVariant, title: string, message: string): void {
    this.alertVariant = variant;
    this.alertTitle = title;
    this.alertMessage = message;
    this.showAlert = true;
  }

  private resetForm(): void {
    this.clientId = 0;
    this.subscriptionId = 0;
    this.vehicleTypeId = 0;
    this.vehicleTypeName = '';
    this.qty = 1;
    this.remark = '';
    this.resultPopup = null;
  }

  private loadAllForSite(siteId: number): void {
    const resUserId = this.resolveUserId();
    if (!resUserId) return;

    this.resetForm();
    this.isLoading.set(true);

    this.apiLegacy.GetClientIInfo(siteId).subscribe({
      next: (res) => this.allClients.set(res ?? []),
      error: () => this.allClients.set([]),
    });

    this.api.getFreeParkingSubscriptions(siteId).subscribe({
      next: (res) => this.allSubscriptions.set(mapFreeParkingSubscriptionList(res)),
      error: () => this.allSubscriptions.set([]),
    });

    this.api.getMyAccessGroupScope(resUserId, siteId).subscribe({
      next: (res) => {
        this.scope.set(mapAccessGroupScope(res));
        this.isLoading.set(false);
      },
      error: () => {
        this.scope.set(null);
        this.isLoading.set(false);
      },
    });
  }

  private resolveUserId(): number {
    const fromLocal = localStorage.getItem('userId');
    if (fromLocal) return Number(fromLocal);
    return Number(this.cookieService.get('user_id')) || 0;
  }
}
